import React, { useState, useEffect, useCallback } from 'react';
import { Button, Typography, IconButton, Dialog, DialogTitle, DialogActions, DialogContent, Grid, Card, CardContent } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Description as DescriptionIcon, Close as CloseIcon } from '@mui/icons-material';
import TemplateAdmin from './TemplateAdmin';
import { TemplateReportService } from '../../../services/templateReportService';
import { openPopup } from '../../../stateManagement/actions/popupActions';
import { GoogleSSOAuthenticationService } from '../../../services/googleAuthenticationService';
import { Loader } from '../../loader/Loader';
import "./TemplateReports.css";
import googleIcon from "../../../assets/image/svg/googleIcon.svg";
import { UI_CONSTANTS } from './constants';
import { GlobalFilterConfig, ColumnMetadata } from './TemplateAdmin';

// TypeScript interfaces for type safety
export interface TemplateOption {
  id: string;
  name: string;
  type: string;
  role: string;
  filter: unknown;
  globalFilters: GlobalFilterConfig[] | null;
  query?: string;
  default_columns?: string;
  all_columns?: Record<string, ColumnMetadata>;
}

export interface UserAuthState {
  isLoggedIn: boolean;
  userEmail: string;
  loading: boolean;
  loginError: string | null;
  openSigninModal: boolean;
}

const ssoConfig = {
  tenant: "salescode_internal",
  appId: "salescode_internal_app",
  apiBaseUrl: "https://dev-auth.salescode.ai",
  tokenEndpoint: "/v1/authenticate/sso/salescode_internal_google/token",
  userInfoEndpoint: "/v1/users/me",
};

const EMPTY_TEMPLATE: TemplateOption = {
  id: '',
  name: '',
  type: 'TemplateReport',
  role: 'admin',
  filter: null,
  globalFilters: [],
  query: '',
  default_columns: '',
  all_columns: {},
};

const TemplateAdminPage: React.FC = () => {
  // Authentication state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [openSigninModal, setOpenSigninModal] = useState(true);

  // Template state
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateOption | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<TemplateOption | null>(null);

  // Auth service instance - use useMemo to prevent recreation
  const authService = React.useMemo(
    () => new GoogleSSOAuthenticationService(ssoConfig),
    []
  );

  const getUserFromToken = useCallback(
    async (token: string) => {
      try {
        const userData = await authService.getUserData(token);
        if (userData && (userData.emailId || userData.email)) {
          const email = userData.emailId || userData.email;
          localStorage.setItem("user_email", email);
          localStorage.setItem("token", token);
          localStorage.setItem("email", email);
          setUserEmail(email);
          setIsLoggedIn(true);
        } else {
          setOpenSigninModal(true);
        }
      } catch (error) {
        setOpenSigninModal(true);
      }
    },
    [authService]
  );

  // Memoize fetchTemplates to avoid unnecessary re-creation
  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const fetchedTemplates = await TemplateReportService.fetchTemplates();
      setTemplates(fetchedTemplates);
    } catch (error) {
      openPopup("Error", "Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  // Refactor: Remove redundant localStorage usage in checkAuthCookie and getUserFromToken
  const checkAuthCookie = useCallback(() => {
    try {
      const storedToken = localStorage.getItem("token");
      const storedEmail = localStorage.getItem("email");
      if (storedToken && storedEmail) {
        setUserEmail(storedEmail);
        setIsLoggedIn(true);
        return true;
      }
      const authCookie = localStorage.getItem("auth_cookie");
      if (!authCookie) {
        setOpenSigninModal(true);
        return false;
      }
      const cookieData = JSON.parse(authCookie);
      if (!cookieData.isAuthenticated) {
        setOpenSigninModal(true);
        return false;
      }
      if (cookieData.expires) {
        const expiryTime = new Date(cookieData.expires);
        const now = new Date();
        if (now > expiryTime) {
          localStorage.removeItem("auth_cookie");
          setOpenSigninModal(true);
          return false;
        }
      }
      const email = localStorage.getItem("user_email");
      if (email && cookieData.token) {
        localStorage.setItem("token", cookieData.token);
        localStorage.setItem("email", email);
        setUserEmail(email);
        setIsLoggedIn(true);
        return true;
      } else if (cookieData.token) {
        getUserFromToken(cookieData.token);
        return true;
      } else {
        setOpenSigninModal(true);
        return false;
      }
    } catch (error) {
      setOpenSigninModal(true);
      return false;
    }
  }, [getUserFromToken]);

  const handleSSOCallback = useCallback(
    async (queryString: string) => {
      try {
        const token = await authService.getToken(queryString);

        if (token) {
          const userData = await authService.getUserData(token);

          if (userData) {
            const now = new Date();
            const expirationTime = new Date(
              now.getTime() + 12 * 60 * 60 * 1000
            );

            // Store auth cookie
            localStorage.setItem(
              "auth_cookie",
              JSON.stringify({
                isAuthenticated: true,
                token: token,
                timestamp: now.toISOString(),
                expires: expirationTime.toISOString(),
                expiresIn: "1d",
                tokenType: "bearer",
              })
            );

            localStorage.setItem("user_email", userData.email || userData.emailId || "");
            localStorage.setItem("token", token);
            localStorage.setItem("email", userData.email || userData.emailId || "");         
            window.history.replaceState({}, document.title, window.location.pathname);
            setUserEmail(userData.email || userData.emailId || "");
            setIsLoggedIn(true);
            setOpenSigninModal(false);
          } else {
            setLoginError("Could not retrieve user information");
            setOpenSigninModal(true);
          }
        } else {
          setLoginError("Failed to authenticate");
          setOpenSigninModal(true);
        }
      } catch (error) {
        setLoginError("Authentication failed");
        setOpenSigninModal(true);
      }
    },
    [authService]
  );

  const checkAuthenticationStatus = useCallback(async () => {
    try {
      setLoading(true);

      // Check query string for SSO callback
      const queryString = window.location.search;

      if (queryString) {
        await handleSSOCallback(queryString);
      } else {
        // Check existing authentication
        checkAuthCookie();
      }
    } catch (error) {
      setOpenSigninModal(true);
    } finally {
      setLoading(false);
    }
  }, [handleSSOCallback, checkAuthCookie]);

  const handleLogin = async () => {
    try {
      setLoading(true);
      setLoginError(null);
      await authService.login();
    } catch (error: unknown) {
      setLoading(false);
      const errorMsg = error instanceof Error ? error.message : "Failed to initiate login";
      setLoginError(errorMsg);
    }
  };

  // Check authentication on component mount
  useEffect(() => {
    checkAuthenticationStatus();
  }, [checkAuthenticationStatus]);

  // Load templates when authenticated
  useEffect(() => {
    if (isLoggedIn) {
      fetchTemplates();
    }
  }, [isLoggedIn]);

  const handleEditTemplate = (template: TemplateOption) => {
    setSelectedTemplate(template);
    setIsCreateMode(false);
    setIsDialogOpen(true);
  };

  const handleCreateTemplate = () => {
    setSelectedTemplate({ ...EMPTY_TEMPLATE });
    setIsCreateMode(true);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedTemplate(null);
    setIsCreateMode(false);
  };

  const handleTemplateUpdated = () => {
    fetchTemplates();
  };

  const handleDeleteTemplate = async (template: TemplateOption) => {
    setTemplateToDelete(template);
  };

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return;
    try {
      await TemplateReportService.deleteTemplate(templateToDelete.name);
      openPopup("Success", `Template '${templateToDelete.name}' deleted successfully`);
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (error) {
      openPopup("Error", `Failed to delete template '${templateToDelete.name}'`);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh"
        }}
        aria-busy="true"
        aria-label="Loading"
      >
        <Loader />
      </div>
    );
  }

  return (
    <div>
      {!isLoggedIn ? (
        <Dialog
          open={openSigninModal}
          aria-labelledby="auth-required-title"
          onClose={(event, reason) => {
            if (reason !== 'backdropClick') setOpenSigninModal(false);
          }}
        >
          <DialogTitle id="auth-required-title">
            Authentication Required
            <IconButton
              aria-label="close"
              onClick={() => setOpenSigninModal(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {loginError && (
              <div style={{ color: "red", marginBottom: "16px" }} role="alert">
                Error: {loginError}
              </div>
            )}
            <p>Please sign in to access the template administration system.</p>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleLogin}
              disabled={loading}
              startIcon={
                <img
                  src={googleIcon}
                  alt="Google logo"
                  style={{ width: 20, height: 20 }}
                />
              }
              aria-label="Sign in with Google"
              sx={{ backgroundColor: '#24c6b1', color: '#fff', '&:hover': { backgroundColor: '#189e91' } }}
            >
              {loading ? "Signing in..." : "Sign in with Google"}
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <div className="template-reports-container">
          <div className="template-reports-header" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '24px 32px',
            background: 'linear-gradient(90deg, #f8fafc 60%, #e0f7f4 100%)',
            borderRadius: 16,
            boxShadow: '0 4px 24px rgba(36,198,177,0.10)',
            marginBottom: 24,
            flexWrap: 'wrap',
          }}>
            <div>
              <Typography variant="h4" component="h1" className="page-title" style={{ marginBottom: 4 }}>
                Create Template
              </Typography>
              <Typography variant="subtitle1" style={{ color: '#64748b', fontWeight: 400 }}>
                Manage your report templates here (Create, delete, or edit templates).
              </Typography>
              {userEmail && (
                <Typography variant="subtitle1" style={{ marginTop: 10, color: '#64748b', fontWeight: 500 }}>
                  Signed in as: {userEmail}
                </Typography>
              )}
            </div>
            <Button
              variant="contained"
              color="primary"
              className="create-btn"
              startIcon={<AddIcon />}
              onClick={handleCreateTemplate}
              style={{
                fontWeight: 700,
                borderRadius: 8,
                fontSize: 16,
                padding: '12px 32px',
                background: 'linear-gradient(90deg, #24c6b1 60%, #189e91 100%)',
                color: '#fff',
                boxShadow: '0 2px 8px rgba(36,198,177,0.10)',
                marginLeft: 24,
                marginTop: 8,
                minWidth: 200,
              }}
            >
              Create New Template
            </Button>
          </div>
          <Card className="template-selection-card">
            <CardContent>
              <Typography variant="h6" gutterBottom color={UI_CONSTANTS.PRIMARY_COLOR}>
                Existing Templates
              </Typography>
              {templates.length === 0 ? (
                <Typography color="textSecondary">
                  No templates found.
                </Typography>
              ) :
                <Grid container spacing={3}>
                  {templates.map((template, idx) => (
                    <Grid item xs={12} sm={6} key={template.name}>
                      <Card
                        elevation={3}
                        style={{
                          background: '#fff',
                          border: '1px solid #e2e8f0',
                          borderRadius: 14,
                          boxShadow: '0 2px 12px 0 rgba(36,198,177,0.08)',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          minHeight: 120,
                          padding: '0',
                          transition: 'box-shadow 0.2s, transform 0.2s',
                          cursor: 'pointer',
                        }}
                        onMouseOver={e => e.currentTarget.style.boxShadow = '0 8px 32px 0 rgba(36,198,177,0.18)'}
                        onMouseOut={e => e.currentTarget.style.boxShadow = '0 2px 12px 0 rgba(36,198,177,0.08)'}
                      >
                        <CardContent style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 16,
                          padding: '28px 24px 8px 24px',
                          flexGrow: 1,
                          borderBottom: '1px solid #f1f5f9',
                        }}>
                          <div style={{
                            background: '#e0f7f4',
                            borderRadius: '50%',
                            width: 38,
                            height: 38,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            <DescriptionIcon style={{ fontSize: 24, color: '#24c6b1' }} />
                          </div>
                          <Typography
                            variant="h6"
                            fontWeight={600}
                            style={{ color: '#1e293b', letterSpacing: 0.1, margin: 0 }}
                            noWrap
                          >
                            {template.name}
                          </Typography>
                        </CardContent>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          gap: 8,
                          padding: '10px 18px 16px 18px',
                        }}>
                          <Button
                            variant="outlined"
                            onClick={() => handleEditTemplate(template)}
                            sx={{
                              borderColor: '#24c6b1',
                              color: '#24c6b1',
                              fontWeight: 500,
                              textTransform: 'none',
                              px: 2.5,
                              minWidth: 64,
                              fontSize: 15,
                              '&:hover': { borderColor: '#189e91', color: '#189e91', background: '#f0fdfa' },
                            }}
                            aria-label="Edit"
                          >
                            Edit
                          </Button>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteTemplate(template)}
                            aria-label="Delete"
                            sx={{
                              background: '#f8fafc',
                              borderRadius: 2,
                              border: '1px solid #e2e8f0',
                              transition: 'background 0.2s',
                              '&:hover': { background: '#fef2f2' },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              }
            </CardContent>
          </Card>
          {isDialogOpen && selectedTemplate && (
            <TemplateAdmin
              template={selectedTemplate}
              onTemplateUpdated={handleTemplateUpdated}
              onClose={handleCloseDialog}
              isCreateMode={isCreateMode}
            />
          )}
          {/* Delete confirmation dialog */}
          <Dialog open={!!templateToDelete} onClose={() => setTemplateToDelete(null)}>
            <DialogTitle>
              Are you sure you want to delete template '{templateToDelete?.name}'?
            </DialogTitle>
            <DialogActions>
              <Button onClick={() => setTemplateToDelete(null)}>Cancel</Button>
              <Button onClick={confirmDeleteTemplate} color="error" variant="contained">
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </div>
  );
};

export default TemplateAdminPage; 